import { type NewContainer } from '@sr/types';

type CustomContainerType = 'other_1' | 'other_2';
const otherLoadCarrierTypes = [
  'bin',
  'box',
  'cage',
  'compactor',
  'container',
  'pallet',
  'sales-packaging',
  'waste-bin',
] as const;

type OtherLoadCarrierType = (typeof otherLoadCarrierTypes)[number];

type ContainerDisplayNameOptions = {
  sourceLabel?: string;
  fallbackDisplayName?: string;
  placeholderDisplayNames?: string[];
};

const otherLoadCarrierTranslationKeyBase =
  'rented-containers.use-container-groups.container.groups';

export const isCustomContainerType = (
  containerType?: string,
): containerType is CustomContainerType => {
  return containerType === 'other_1' || containerType === 'other_2';
};

const isOtherLoadCarrierType = (
  otherLoadCarrierType?: string,
): otherLoadCarrierType is OtherLoadCarrierType => {
  return (
    !!otherLoadCarrierType && otherLoadCarrierTypes.some((type) => type === otherLoadCarrierType)
  );
};

export const getOtherLoadCarrierTranslationKey = (otherLoadCarrierType?: string) => {
  if (!isOtherLoadCarrierType(otherLoadCarrierType)) {
    return undefined;
  }

  return `${otherLoadCarrierTranslationKeyBase}.${otherLoadCarrierType}`;
};

const normalizeDisplayName = (value?: string) => value?.trim().toLocaleLowerCase();

const isPlaceholderDisplayName = (
  displayName: string | undefined,
  placeholderDisplayNames: string[],
) => {
  const normalizedDisplayName = normalizeDisplayName(displayName);

  if (!normalizedDisplayName) {
    return false;
  }

  return placeholderDisplayNames.some(
    (placeholderDisplayName) =>
      normalizedDisplayName === normalizeDisplayName(placeholderDisplayName),
  );
};

export const getContainerDisplayName = (
  container: Pick<
    NewContainer,
    'containerType' | 'containerName' | 'otherLoadCarrierName' | 'otherLoadCarrierType'
  >,
  options: ContainerDisplayNameOptions = {},
) => {
  if (!isCustomContainerType(container.containerType)) {
    return container.containerName ?? '';
  }

  const placeholderDisplayNames = [
    container.containerName ?? '',
    ...(options.placeholderDisplayNames ?? []),
  ];
  const selectedOtherLoadCarrierName = container.otherLoadCarrierName?.trim();
  const fallbackDisplayName = options.fallbackDisplayName?.trim();

  const selectedContainerName =
    selectedOtherLoadCarrierName &&
    !isPlaceholderDisplayName(selectedOtherLoadCarrierName, placeholderDisplayNames)
      ? selectedOtherLoadCarrierName
      : fallbackDisplayName || container.containerName?.trim();

  if (!selectedContainerName) {
    return '';
  }

  const sourceLabel = options.sourceLabel?.trim();

  if (!sourceLabel) {
    return selectedContainerName;
  }

  return `${selectedContainerName} (${sourceLabel})`;
};
